import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';

const EventsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para eventos da ronda selecionada
  const [selectedRonda, setSelectedRonda] = useState(null);
  const [rondaEvents, setRondaEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  // Estado para edição de evento
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await adminApi.getEventsStats();
      setStats(response.data.stats || response.data);
    } catch (err) {
      console.error('Error fetching events stats:', err);
      setError(err.response?.data?.message || 'Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchRondaEvents = async (rondaId) => {
    setLoadingEvents(true);
    try {
      const response = await adminApi.getEvents({ ronda_id: rondaId, limit: 100 });
      setRondaEvents(response.data.data || []);
    } catch (err) {
      console.error('Error fetching ronda events:', err);
      setRondaEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleRondaClick = (ronda) => {
    if (selectedRonda?.ronda_codigo === ronda.ronda_codigo) {
      setSelectedRonda(null);
      setRondaEvents([]);
    } else {
      setSelectedRonda(ronda);
      fetchRondaEvents(ronda.ronda_codigo);
    }
  };

  const handleEditClick = (event) => {
    setEditingEvent(event);
    // Format date for datetime-local input (YYYY-MM-DDTHH:MM) em hora local,
    // para coincidir com as horas mostradas no resto do painel
    let dateValue = '';
    if (event.created_at) {
      const d = new Date(event.created_at);
      const pad = (n) => String(n).padStart(2, '0');
      dateValue = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    setEditForm({
      tipo: event.tipo || '',
      local: event.local || '',
      latitude: event.latitude || '',
      longitude: event.longitude || '',
      pessoas_assistidas: event.pessoas_assistidas || 0,
      numero_refeicoes: event.numero_refeicoes || 0,
      voluntarios_presentes: event.voluntarios_presentes || 0,
      total_kms: event.total_kms || 0,
      tempo_viagem: event.tempo_viagem || 0,
      tempo_paragem: event.tempo_paragem || 0,
      created_at: dateValue,
      notas: event.notas || ''
    });
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!editingEvent) return;
    
    setSaving(true);
    try {
      // O datetime-local é interpretado pelo browser como hora local; enviar
      // ISO com timezone para o backend gravar o instante correto
      const payload = { ...editForm };
      if (payload.created_at) {
        payload.created_at = new Date(payload.created_at).toISOString();
      }
      await adminApi.updateEvent(editingEvent.id, payload);
      setEditingEvent(null);
      // Recarregar eventos da ronda
      if (selectedRonda) {
        fetchRondaEvents(selectedRonda.ronda_codigo);
      }
      // Recarregar estatísticas
      fetchStats();
    } catch (err) {
      console.error('Error updating event:', err);
      alert(err.response?.data?.message || 'Erro ao guardar evento');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setEditForm({});
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoBadgeClass = (tipo) => {
    switch (tipo) {
      case 'inicio': return 'badge approved';
      case 'final': return 'badge';
      case 'paragem': return 'badge primary';
      case 'emergencia': return 'badge rejected';
      default: return 'badge';
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">Carregando estatísticas de eventos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">
          <h3>Erro ao carregar estatísticas</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchStats}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>Sem dados disponíveis</h3>
          <p>Nenhuma estatística de eventos encontrada.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Summary Cards */}
      <div className="card">
        <div className="card-header">
          <h3>Resumo de Eventos</h3>
          <button className="btn btn-primary btn-sm" onClick={fetchStats}>
            Atualizar
          </button>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          padding: '1rem'
        }}>
          <div className="stat-card">
            <h4>Eventos Hoje</h4>
            <div className="stat-number">{stats.today?.total_today || 0}</div>
          </div>

          <div className="stat-card">
            <h4>Rondas Hoje</h4>
            <div className="stat-number">{stats.today?.rondas_today || 0}</div>
          </div>

          <div className="stat-card">
            <h4>Pessoas Assistidas Hoje</h4>
            <div className="stat-number">{stats.today?.pessoas_assistidas_today || 0}</div>
          </div>

          <div className="stat-card">
            <h4>Refeições Servidas Hoje</h4>
            <div className="stat-number">{stats.today?.refeicoes_servidas_today || 0}</div>
          </div>

          <div className="stat-card">
            <h4>Condutores Ativos</h4>
            <div className="stat-number">{stats.today?.condutores_today || 0}</div>
          </div>
        </div>
      </div>

      {/* Events by Type */}
      <div className="card">
        <div className="card-header">
          <h3>Eventos por Tipo (Últimos 30 dias)</h3>
        </div>

        {!stats.by_type || stats.by_type.length === 0 ? (
          <div className="empty-state">
            <p>Nenhum evento registado ainda.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_type.map((type, index) => (
                  <tr key={index}>
                    <td><strong>{type.tipo}</strong></td>
                    <td>
                      <span className="badge primary">{type.count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Active Rondas */}
      <div className="card">
        <div className="card-header">
          <h3>Rondas Mais Ativas (Últimos 30 dias)</h3>
          <small style={{ color: '#6c757d' }}>Clique numa ronda para ver/editar eventos</small>
        </div>

        {!stats.active_rondas || stats.active_rondas.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma ronda ativa recentemente.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Eventos</th>
                  <th>Pessoas Assistidas</th>
                  <th>Refeições Servidas</th>
                </tr>
              </thead>
              <tbody>
                {stats.active_rondas.map((ronda, index) => (
                  <React.Fragment key={index}>
                    <tr 
                      onClick={() => handleRondaClick(ronda)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedRonda?.ronda_codigo === ronda.ronda_codigo ? '#e3f2fd' : 'inherit'
                      }}
                    >
                      <td><strong>{ronda.ronda_codigo}</strong></td>
                      <td>{ronda.ronda_name}</td>
                      <td>
                        <span className="badge primary">{ronda.event_count}</span>
                      </td>
                      <td>
                        <span className="badge approved">{ronda.total_pessoas_assistidas}</span>
                      </td>
                      <td>
                        <span className="badge" style={{ backgroundColor: '#17a2b8' }}>
                          {ronda.total_refeicoes_servidas || 0}
                        </span>
                      </td>
                    </tr>
                    
                    {/* Eventos da ronda selecionada */}
                    {selectedRonda?.ronda_codigo === ronda.ronda_codigo && (
                      <tr>
                        <td colSpan="5" style={{ padding: 0, backgroundColor: '#f8f9fa' }}>
                          <div style={{ padding: '1rem' }}>
                            <h4 style={{ marginBottom: '0.5rem' }}>
                              Eventos de {ronda.ronda_name}
                              <button 
                                className="btn btn-sm"
                                onClick={(e) => { e.stopPropagation(); setSelectedRonda(null); }}
                                style={{ marginLeft: '1rem' }}
                              >
                                Fechar
                              </button>
                            </h4>
                            
                            {loadingEvents ? (
                              <p>Carregando eventos...</p>
                            ) : rondaEvents.length === 0 ? (
                              <p>Nenhum evento encontrado.</p>
                            ) : (
                              <table className="table" style={{ marginBottom: 0 }}>
                                <thead>
                                  <tr>
                                    <th>Data/Hora</th>
                                    <th>Tipo</th>
                                    <th>Local</th>
                                    <th>Pessoas</th>
                                    <th>Refeições</th>
                                    <th>Voluntários</th>
                                    <th>Ações</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rondaEvents.map((event) => (
                                    <tr key={event.id}>
                                      <td>{formatShortDate(event.created_at)}</td>
                                      <td>
                                        <span className={getTipoBadgeClass(event.tipo)}>
                                          {event.tipo}
                                        </span>
                                      </td>
                                      <td>{event.local || '-'}</td>
                                      <td>{event.pessoas_assistidas || 0}</td>
                                      <td>{event.numero_refeicoes || 0}</td>
                                      <td>{event.voluntarios_presentes || 0}</td>
                                      <td>
                                        <button 
                                          className="btn btn-sm btn-primary"
                                          onClick={(e) => { e.stopPropagation(); handleEditClick(event); }}
                                        >
                                          ✏️ Editar
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weekly and Monthly Stats */}
      <div className="card">
        <div className="card-header">
          <h3>Estatísticas Semanais e Mensais</h3>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          padding: '1rem'
        }}>
          <div className="stat-card">
            <h4>Esta Semana</h4>
            <div className="stat-detail">
              <span>Eventos: </span>
              <strong>{stats.week?.total_week || 0}</strong>
            </div>
            <div className="stat-detail">
              <span>Rondas Ativas: </span>
              <strong>{stats.week?.rondas_week || 0}</strong>
            </div>
            <div className="stat-detail">
              <span>Pessoas Assistidas: </span>
              <strong>{stats.week?.pessoas_assistidas_week || 0}</strong>
            </div>
            <div className="stat-detail">
              <span>Refeições Servidas: </span>
              <strong>{stats.week?.refeicoes_servidas_week || 0}</strong>
            </div>
          </div>

          <div className="stat-card">
            <h4>Este Mês</h4>
            <div className="stat-detail">
              <span>Eventos: </span>
              <strong>{stats.month?.total_month || 0}</strong>
            </div>
            <div className="stat-detail">
              <span>Rondas Ativas: </span>
              <strong>{stats.month?.rondas_month || 0}</strong>
            </div>
            <div className="stat-detail">
              <span>Pessoas Assistidas: </span>
              <strong>{stats.month?.pessoas_assistidas_month || 0}</strong>
            </div>
            <div className="stat-detail">
              <span>Refeições Servidas: </span>
              <strong>{stats.month?.refeicoes_servidas_month || 0}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.9rem' }}>
        Última atualização: {new Date().toLocaleString('pt-PT')}
      </div>

      {/* Modal de Edição */}
      {editingEvent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={handleCancelEdit}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Editar Evento #{editingEvent.id}</h3>
            <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
              {formatDate(editingEvent.created_at)}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Data/Hora</label>
                  <input 
                    type="datetime-local"
                    value={editForm.created_at}
                    onChange={(e) => handleFormChange('created_at', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Tipo</label>
                  <select 
                    value={editForm.tipo}
                    onChange={(e) => handleFormChange('tipo', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="inicio">Início</option>
                    <option value="paragem">Paragem</option>
                    <option value="emergencia">Emergência</option>
                    <option value="final">Final</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Local</label>
                <input 
                  type="text"
                  value={editForm.local}
                  onChange={(e) => handleFormChange('local', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Latitude</label>
                  <input 
                    type="number"
                    step="any"
                    value={editForm.latitude}
                    onChange={(e) => handleFormChange('latitude', parseFloat(e.target.value) || '')}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Longitude</label>
                  <input 
                    type="number"
                    step="any"
                    value={editForm.longitude}
                    onChange={(e) => handleFormChange('longitude', parseFloat(e.target.value) || '')}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Pessoas</label>
                  <input 
                    type="number"
                    min="0"
                    value={editForm.pessoas_assistidas}
                    onChange={(e) => handleFormChange('pessoas_assistidas', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Refeições</label>
                  <input 
                    type="number"
                    min="0"
                    max="9999"
                    value={editForm.numero_refeicoes}
                    onChange={(e) => handleFormChange('numero_refeicoes', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Voluntários</label>
                  <input 
                    type="number"
                    min="0"
                    value={editForm.voluntarios_presentes}
                    onChange={(e) => handleFormChange('voluntarios_presentes', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Km</label>
                  <input 
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.total_kms}
                    onChange={(e) => handleFormChange('total_kms', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Viagem (min)</label>
                  <input 
                    type="number"
                    min="0"
                    value={editForm.tempo_viagem}
                    onChange={(e) => handleFormChange('tempo_viagem', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Paragem (min)</label>
                  <input 
                    type="number"
                    min="0"
                    value={editForm.tempo_paragem}
                    onChange={(e) => handleFormChange('tempo_paragem', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Notas</label>
                <textarea 
                  value={editForm.notas}
                  onChange={(e) => handleFormChange('notas', e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button 
                className="btn"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsDashboard;